import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { FiMail, FiPhone, FiMapPin, FiSend } from "react-icons/fi";
import { FaFacebook, FaInstagram, FaTiktok } from "react-icons/fa";
import { SiGoodreads } from "react-icons/si";
import emailjs from "@emailjs/browser";
import toast from "react-hot-toast";
import SEO from "../components/SEO";

export default function Contact({ generalConfig }) {
    const config = generalConfig || {};
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const formRef = React.useRef();

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "contact"));
                if (snap.exists()) setData(snap.data());
            } catch (e) {
                console.error("Error loading contact page:", e);
            }
            setLoading(false);
        }
        load();
    }, []);

    async function handleFormSubmit(e) {
        e.preventDefault();
        
        if (!data?.emailjsServiceId || !data?.emailjsTemplateId || !data?.emailjsPublicKey) {
            toast.error("Contact form is not configured yet.");
            return;
        }

        setSending(true);
        try {
            // Initialize EmailJS with the public key from Firestore
            emailjs.init(data.emailjsPublicKey);

            await emailjs.sendForm(
                data.emailjsServiceId,
                data.emailjsTemplateId,
                formRef.current,
                data.emailjsPublicKey
            );
            toast.success("Message sent successfully!");
            e.target.reset();
        } catch (error) {
            console.error("EmailJS Error:", error);
            toast.error("Failed to send. Please verify your Public Key in the Admin panel.");
        }
        setSending(false);
    }

    if (loading) return (
        <div className="container" style={{ padding: "100px 20px", textAlign: "center", color: "#888" }}>
            Loading contact page...
        </div>
    );

    return (
        <div className="contact-page">
            <SEO pageId="contact" />
            <div className="container" style={{ padding: "80px 20px" }}>
                <div style={{ textAlign: "center", maxWidth: 700, margin: "0 auto 80px" }}>
                    <h1 style={{ fontSize: "3rem", fontFamily: "'Playfair Display', serif", marginBottom: 20 }}>Get in Touch</h1>
                    <p style={{ fontSize: "1.1rem", color: "#666", lineHeight: 1.6 }}>
                        {data?.inquiryNote || "Have a question about a book, a signed copy request, or just want to say hi? I'd love to hear from you."}
                    </p>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 60 }}>
                    
                    {/* Info Side */}
                    <div style={{ flex: "1 1 350px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                            <div className="contact-info-card">
                                <div className="contact-icon-wrap"><FiMail size={22} /></div>
                                <div>
                                    <h4 style={{ margin: "0 0 4px" }}>Email</h4>
                                    <a href={`mailto:${data?.email || 'hello@lbbooks.com'}`} style={{ color: "var(--accent)", fontWeight: 600, textDecoration: "none" }}>
                                        {data?.email || "hello@lbbooks.com"}
                                    </a>
                                </div>
                            </div>
                            {data?.phone && (
                                <div className="contact-info-card">
                                    <div className="contact-icon-wrap"><FiPhone size={22} /></div>
                                    <div>
                                        <h4 style={{ margin: "0 0 4px" }}>Phone</h4>
                                        <p style={{ color: "#555", margin: 0 }}>{data.phone}</p>
                                    </div>
                                </div>
                            )}
                            <div className="contact-info-card">
                                <div className="contact-icon-wrap"><FiMapPin size={22} /></div>
                                <div>
                                    <h4 style={{ margin: "0 0 4px" }}>Location</h4>
                                    <p style={{ color: "#555", margin: 0 }}>{data?.address || "San Juan, Puerto Rico"}</p>
                                </div>
                            </div>

                            <hr style={{ border: 0, borderTop: "1px solid #eee", margin: "10px 0" }} />

                            <div>
                                <h4 style={{ margin: "0 0 16px", fontSize: 14, textTransform: "uppercase", letterSpacing: 1, color: "#888" }}>
                                    {config.socialTitle || "Follow Me"}
                                </h4>
                                <div style={{ display: "flex", gap: 15 }}>
                                    {config.facebookUrl && (
                                        <a href={config.facebookUrl} target="_blank" rel="noreferrer" className="social-link-btn" aria-label="Facebook">
                                            <FaFacebook size={20} />
                                        </a>
                                    )}
                                    {config.instagramUrl && (
                                        <a href={config.instagramUrl} target="_blank" rel="noreferrer" className="social-link-btn" aria-label="Instagram">
                                            <FaInstagram size={20} />
                                        </a>
                                    )}
                                    {config.tiktokUrl && (
                                        <a href={config.tiktokUrl} target="_blank" rel="noreferrer" className="social-link-btn" aria-label="TikTok">
                                            <FaTiktok size={20} />
                                        </a>
                                    )}
                                    {config.goodreadsUrl && (
                                        <a href={config.goodreadsUrl} target="_blank" rel="noreferrer" className="social-link-btn" aria-label="Goodreads">
                                            <SiGoodreads size={20} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Side */}
                    <div style={{ flex: "2 1 500px" }}>
                        <div style={{ background: "#fff", padding: 40, borderRadius: 20, boxShadow: "0 10px 40px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}>
                            <form ref={formRef} onSubmit={handleFormSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                                <div style={{ gridColumn: "1 / span 1" }}>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#333" }}>Full Name</label>
                                    <input required name="user_name" placeholder="Jane Doe" style={inputStyle} />
                                </div>
                                <div style={{ gridColumn: "2 / span 1" }}>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#333" }}>Email Address</label>
                                    <input required name="user_email" type="email" placeholder="jane@example.com" style={inputStyle} />
                                </div>
                                <div style={{ gridColumn: "1 / -1" }}>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#333" }}>Subject</label>
                                    <input required name="subject" placeholder="Signed Copy Inquiry" style={inputStyle} />
                                </div>
                                <div style={{ gridColumn: "1 / -1" }}>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#333" }}>Message</label>
                                    <textarea required name="message" placeholder="How can I help you?" style={{ ...inputStyle, height: 150, resize: "none" }} />
                                </div>
                                <div style={{ gridColumn: "1 / -1" }}>
                                    <button 
                                        type="submit" 
                                        disabled={sending}
                                        style={{ 
                                            background: "var(--green-primary)", color: "white", border: "none", 
                                            padding: "16px 32px", borderRadius: 12, fontWeight: 700, 
                                            cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                                            width: "100%", justifyContent: "center", transition: "all 0.3s"
                                        }}
                                        className="contact-submit-btn"
                                    >
                                        {sending ? "Sending..." : <>Send Message <FiSend size={18} /></>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                </div>
            </div>

            <style>{`
                .contact-info-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 20px;
                }
                .contact-icon-wrap {
                    width: 50px;
                    height: 50px;
                    background: #fdf8e6;
                    color: var(--accent);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .contact-submit-btn:hover {
                    background: var(--green-dark);
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(13, 74, 53, 0.2);
                }
                .social-link-btn {
                    width: 44px;
                    height: 44px;
                    background: #f8f9fa;
                    color: #333;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    border: 1px solid #eee;
                }
                .social-link-btn:hover {
                    background: var(--green-primary);
                    color: white;
                    transform: translateY(-3px);
                    border-color: var(--green-primary);
                    box-shadow: 0 5px 15px rgba(13, 74, 53, 0.2);
                }
                @media (max-width: 600px) {
                    .contact-page form { grid-template-columns: 1fr !important; }
                    .contact-page form > div { grid-column: 1 / -1 !important; }
                }
            `}</style>
        </div>
    );
}

const inputStyle = {
    width: "100%",
    padding: "14px 18px",
    borderRadius: 10,
    border: "1.5px solid #eee",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.3s",
    background: "#fafafa"
};
